import React, { useState } from 'react'
import styles from './GroupedWorklogsView.module.css'
import { formatDisplayDate, moment } from '../utils/dateUtils'

export default function GroupedWorklogsView({ groupedData, groupByFields, onWorklogClick, onGroupClick }) {
    const [expandedGroups, setExpandedGroups] = useState(new Set())
    
    const toggleGroup = (groupPath) => {
        const newExpanded = new Set(expandedGroups)
        if (newExpanded.has(groupPath)) {
            newExpanded.delete(groupPath)
        } else {
            newExpanded.add(groupPath)
        }
        setExpandedGroups(newExpanded)
    }
    
    const handleGroupClick = (groupField, groupKey, level, parentPath) => {
        // Build the complete filter hierarchy from the path
        const filterHierarchy = []
        
        // Parse the path to get all parent filters
        if (parentPath) {
            const pathParts = parentPath.split('/')
            pathParts.forEach((part, index) => {
                if (part && groupByFields[index]) {
                    filterHierarchy.push({
                        field: groupByFields[index],
                        value: part
                    })
                }
            })
        }
        
        // Add the current group filter
        filterHierarchy.push({
            field: groupField,
            value: groupKey
        })
        
        if (onGroupClick) {
            onGroupClick(filterHierarchy)
        }
    }
    
    const renderGroupLevel = (data, level = 0, parentPath = '') => {
        if (!data || typeof data !== 'object') {
            return null
        }
        
        // If this is a worklog item (has an id), render it
        if (data.id) {
            return renderWorklogItem(data)
        }
        
        // If this is grouped data, render the groups
        // Sort alphabetically by groupKey
        return Object.entries(data).sort(([keyA], [keyB]) => {
            // Handle null/undefined values
            const displayNameA = getGroupDisplayName(groupByFields[level], keyA) || ''
            const displayNameB = getGroupDisplayName(groupByFields[level], keyB) || ''
            return displayNameA.localeCompare(displayNameB, undefined, { sensitivity: 'base' })
        }).map(([groupKey, groupValue]) => {
            const groupPath = parentPath ? `${parentPath}/${groupKey}` : groupKey
            const isExpanded = expandedGroups.has(groupPath)
            
            // Calculate totals for this group
            const totals = calculateGroupTotals(groupValue)
            
            // Check if this group contains worklog items directly (final level)
            const hasDirectWorklogs = Object.values(groupValue).some(item => item && item.id)
            
            return (
                <div key={groupPath} className={styles.groupContainer}>
                    <div 
                        className={`${styles.groupHeader} ${styles[`level${level}`]}`}
                        onClick={() => {
                            if (hasDirectWorklogs) {
                                // Final level - go to detailed view
                                handleGroupClick(groupByFields[level], groupKey, level, parentPath)
                            } else {
                                // Intermediate level - toggle expansion
                                toggleGroup(groupPath)
                            }
                        }}
                    >
                        <div className={styles.groupTitleSection}>
                            {!hasDirectWorklogs && (
                                <span className={styles.expandIcon}>
                                    {isExpanded ? '▼' : '▶'}
                                </span>
                            )}
                            <span className={styles.groupCount}>{totals.count}</span>
                            <span className={styles.groupTitle}>
                                {getGroupDisplayName(groupByFields[level], groupKey)}
                            </span>
                        </div>
                        <div className={styles.groupSummary}>
                            <span className={styles.groupDuration}>
                                {totals.duration}
                            </span>
                        </div>
                    </div>
                    
                    {!hasDirectWorklogs && isExpanded && (
                        <div className={styles.groupContent}>
                            {renderGroupLevel(groupValue, level + 1, groupPath)}
                        </div>
                    )}
                </div>
            )
        })
    }
    
    const renderWorklogItem = (worklog) => {
        return (
            <div 
                key={worklog.id} 
                className={styles.worklogItem}
                onClick={() => onWorklogClick && onWorklogClick(worklog)}
            >
                <div className={styles.worklogDetails}>
                    <div className={styles.worklogMainInfo}>
                        <span className={styles.worklogUser}>
                            {worklog.user?.name}
                        </span>
                        <span className={styles.worklogDescription}>
                            {worklog.description || 'No description'}
                        </span>
                    </div>
                    <div className={styles.worklogMetaInfo}>
                        <span className={styles.worklogProject}>
                            {worklog.project?.name}
                        </span>
                        {worklog.task?.name && (
                            <span className={styles.worklogTask}>
                                • {worklog.task.name}
                            </span>
                        )}
                        <span className={styles.worklogDate}>
                            {formatDisplayDate(worklog.date)}
                        </span>
                    </div>
                </div>
                <div className={styles.worklogDuration}>
                    {worklog.duration_formatted}
                </div>
            </div>
        )
    }
    
    const calculateGroupTotals = (data) => {
        let totalSeconds = 0
        let directChildrenCount = 0
        
        if (data && typeof data === 'object') {
            // Count direct children and calculate total duration
            Object.values(data).forEach(child => {
                if (child && typeof child === 'object') {
                    if (child.id) {
                        // This is a worklog item - count it as direct child
                        directChildrenCount++
                        totalSeconds += child.duration_seconds || 0
                    } else {
                        // This is a group - count it as direct child and sum its duration
                        directChildrenCount++
                        const childTotals = calculateGroupTotalsRecursive(child)
                        totalSeconds += childTotals.totalSeconds
                    }
                }
            })
        }
        
        return {
            count: directChildrenCount,
            duration: formatDuration(totalSeconds)
        }
    }
    
    const calculateGroupTotalsRecursive = (data) => {
        let totalSeconds = 0
        
        const traverse = (obj) => {
            if (obj && typeof obj === 'object') {
                if (obj.id) {
                    // This is a worklog item
                    totalSeconds += obj.duration_seconds || 0
                } else {
                    // This is a group, traverse its children
                    Object.values(obj).forEach(traverse)
                }
            }
        }
        
        traverse(data)
        
        return { totalSeconds }
    }
    
    const formatDuration = (seconds) => {
        if (!seconds) return '00:00:00'
        
        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        const remainingSeconds = seconds % 60
        
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    }
    
    const getGroupDisplayName = (groupField, groupKey) => {
        if (!groupKey || groupKey === 'null' || groupKey === 'undefined') {
            return `No ${groupField}`
        }
        
        // Format different group types
        switch (groupField) {
            case 'date':
                return formatDisplayDate(groupKey)
            case 'week':
                return `Week of ${groupKey}`
            case 'month':
                return moment(groupKey + '-01').format('MMMM YYYY')
            case 'billable':
                return groupKey === 'true' ? 'Billable' : 'Non-billable'
            default:
                return groupKey
        }
    }
    
    if (!groupedData || Object.keys(groupedData).length === 0) {
        return (
            <div className={styles.noData}>
                No data available for the selected grouping.
            </div>
        )
    }
    
    return (
        <div className={styles.groupedView}>
            {renderGroupLevel(groupedData)}
        </div>
    )
}